import Layout from "../../components/Layout.tsx";
import { StepProps } from "./AddNewConnection.tsx";
import { Button } from "../../components/Button.tsx";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import toast from "react-hot-toast";
import { ExactOnlineAuthService } from "../../services/ExactOnlineAuthService";
import { FieldGroup, Field, Description, Label } from "../../components/Fieldset";
import AdministrationBadgeList from "../../components/AdministrationBadgeList.tsx";
import { useNewConnectionContext } from "../../context/NewConnectionContext.tsx";
import { useOAuthPopupCallback, openOAuthPopup } from "../../hooks/useOAuthPopup";
import { useExactSystemAuthorizations } from "../../hooks/data.ts";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "../../components/Table.tsx";

const AuthenticateStep = ({ handleButtonBackClick, handleButtonNextClick, stepIndicator }: StepProps) => {
  const [isChecking, setIsChecking] = useState(false);
  const authService = new ExactOnlineAuthService();
  const [, setLocation] = useLocation();

  const { newConnectionData, dispatch } = useNewConnectionContext();
  const isUpdating = newConnectionData.id && newConnectionData.id > 0;
  const { systemAuthorizations, loading } = useExactSystemAuthorizations();
  const [selectedSystemAuthorizationId, setSelectedSystemAuthorizationId] = useState<number | null>(
    newConnectionData.systemAuthorizationId || null
  );

  // If this window was opened as an OAuth popup, forward result and close
  useOAuthPopupCallback();

  useEffect(() => {
    if (newConnectionData.systemAuthorizationId) {
      setSelectedSystemAuthorizationId(newConnectionData.systemAuthorizationId);
    }
  }, [newConnectionData.systemAuthorizationId]);

  useEffect(() => {
    if (!loading && systemAuthorizations.length > 0 && !newConnectionData.systemAuthorizationId) {
      const firstId = systemAuthorizations[0].id;
      setSelectedSystemAuthorizationId(firstId);
      dispatch({ type: "UPDATE_FIELD", key: "systemAuthorizationId", value: firstId });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- listing this would re-run the effect on changes it should ignore
  }, [loading, systemAuthorizations]);

  const selectSystemAuthorization = (id: number) => {
    setSelectedSystemAuthorizationId(id);
    dispatch({ type: "UPDATE_FIELD", key: "systemAuthorizationId", value: id });
  };

  const handleAuthSuccess = (systemAuthorizationId?: number) => {
    if (systemAuthorizationId) {
      dispatch({
        type: "UPDATE_FIELD",
        key: "systemAuthorizationId",
        value: systemAuthorizationId,
      });
    }
    handleButtonNextClick?.();
  };

  const getAuthenticateCode = async (existingSystemAuthorizationId?: number) => {
    try {
      // Determine redirect destination based on whether we're updating or creating
      const redirectTo = isUpdating ? `koppeling/${newConnectionData.id}` : "nieuwe-koppeling";
      const response = await authService.getAuthorizationUrl(redirectTo, existingSystemAuthorizationId);

      if (!response.success) {
        toast.error(response.error || "Er is een fout opgetreden");
        return;
      }

      if (response.data && typeof response.data === 'string') {
        // Need OAuth popup
        toast(response.message || "Authenticatie vereist");
        try {
          const result = await openOAuthPopup(response.data);
          if (result.status === "success") {
            toast.success(result.message || "Authenticatie succesvol");
            handleAuthSuccess(result.systemAuthorizationId);
          } else {
            toast.error(result.message || "Er is een fout opgetreden");
          }
        } catch {
          toast.error("Authenticatie popup gesloten of geblokkeerd");
        }
      } else if (response.data && typeof response.data === 'object') {
        // Already authenticated, backend returned { message, system_authorization_id }
        handleAuthSuccess(response.data.system_authorization_id);
      } else {
        toast.success(response.message || "Authenticatie succesvol");
        handleAuthSuccess();
      }
    } catch (error) {
      toast.error("Er is een onverwachte fout opgetreden");
      console.error("Authentication error:", error);
    }
  }

  const onButtonNextClick = async () => {
    if (!selectedSystemAuthorizationId) {
      return;
    }
    setIsChecking(true);
    try {
      await getAuthenticateCode(selectedSystemAuthorizationId);
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <Layout
      title={isUpdating ? "Koppeling aanpassen" : "Nieuwe koppeling maken"}
      stepTitle="Authenticeren"
      onButtonBackClick={handleButtonBackClick}
      onButtonNextClick={onButtonNextClick}
      enableNextButton={!!selectedSystemAuthorizationId && !isChecking}
      stepIndicator={stepIndicator}
    >
      <FieldGroup>
        <Field>
          <Description className="text-sm pb-2">
            Kies een bestaande Exact Online-authenticatie.
            <br />
            Een authenticatie kan gebruikt worden in meerdere koppelingen.
            <br />
            Alleen authenticaties die voor jou beschikbaar zijn, worden getoond.
          </Description>
          <div className="grid gap-3 max-w-2xl">
            <Label>Exact Online authenticatie</Label>
            {loading ? (
              <div className="rounded-lg border border-zinc-950/10 px-4 py-8 text-center text-sm text-zinc-500">
                Authenticaties laden...
              </div>
            ) : systemAuthorizations.length === 0 ? (
              <div className="flex flex-col items-center gap-3 rounded-lg border border-zinc-950/10 px-4 py-8 text-center text-sm text-zinc-500">
                <p>Je hebt nog geen Exact Online-authenticatie.</p>
                <p>Maak er eerst een aan op de Authenticatie-pagina.</p>
                <Button outline onClick={() => setLocation("/authenticatie")}>
                  Naar Authenticatie
                </Button>
              </div>
            ) : (
              <Table dense grid className="rounded-lg border border-zinc-950/10">
                <TableHead>
                  <TableRow>
                    <TableHeader className="w-10 !px-2">
                      <span className="sr-only">Selecteren</span>
                    </TableHeader>
                    <TableHeader>Authenticatie</TableHeader>
                    <TableHeader>Beschikbare administraties</TableHeader>
                    <TableHeader>Actieve koppelingen</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {systemAuthorizations.map((systemAuthorization) => {
                    const isSelected = selectedSystemAuthorizationId === systemAuthorization.id;
                    const label = systemAuthorization.label ?? `Exact authenticatie ${systemAuthorization.id}`;
                    return (
                      <TableRow
                        key={systemAuthorization.id}
                        onClick={() => selectSystemAuthorization(systemAuthorization.id)}
                        className={`cursor-pointer ${isSelected ? "bg-cta/5" : "hover:bg-zinc-950/[2.5%]"}`}
                      >
                        <TableCell className="!px-2">
                          <div className="flex items-center justify-center">
                            <input
                              type="radio"
                              name="systemAuthorization"
                              aria-label={label}
                              checked={isSelected}
                              onChange={() => selectSystemAuthorization(systemAuthorization.id)}
                              className="size-4 border-zinc-300 text-cta focus:ring-cta"
                            />
                          </div>
                        </TableCell>
                        <TableCell className="font-medium text-zinc-950">{label}</TableCell>
                        <TableCell>
                          <AdministrationBadgeList administrations={systemAuthorization.administrations} />
                        </TableCell>
                        <TableCell className="text-zinc-500">
                          <div className="flex flex-col gap-1">
                            {systemAuthorization.connectionNames.length === 0 ? (
                              <span className="text-zinc-400">Geen</span>
                            ) : (
                              systemAuthorization.connectionNames.map((connectionName, index) => (
                                <span key={`${connectionName}-${index}`} className="whitespace-nowrap">
                                  {connectionName}
                                </span>
                              ))
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </Field>
      </FieldGroup>
    </Layout>
  );
};

export default AuthenticateStep;
